import { apiPost } from '@services/apiClient'
import { authedFetch } from '@features/auth/api/authRefresh'
import type { TwoFactorLoginVerifyResponse } from '@/crypto/key-hierarchy.types'

/**
 * 2FA TOTP REST client (Phase 4 #44、backend `apps/api/src/two-factor/` と 1:1)。
 *
 * 設定画面系 (status/setup/verify-enable/disable/regenerate) は `JwtAuthGuard`
 * 保護のため `authedFetch` 経由 (401 retry を伴う)。
 * `loginVerify` は SRP login で受け取った短期 pre-auth token を Bearer で送る
 * 別経路 (まだ access token は無い)。
 */

const BASE = '/auth/2fa'

export interface TwoFactorStatusResponse {
  readonly enabled: boolean
  readonly pending: boolean
  readonly recoveryCodesRemaining: number
}

export interface TwoFactorSetupResponse {
  readonly otpAuthUri: string
  readonly secretBase32: string
}

export interface TwoFactorEnableResponse extends TwoFactorStatusResponse {
  /** verify-enable 成功時の 1 回限り発行される recovery codes (平文)。 */
  readonly recoveryCodes: string[]
}

export interface TwoFactorRegenerateResponse {
  readonly codes: string[]
}

/** GET /auth/2fa/status — 現在の 2FA 状態 */
export const getTwoFactorStatus = (): Promise<TwoFactorStatusResponse> =>
  authedFetch<TwoFactorStatusResponse>(`${BASE}/status`)

/** POST /auth/2fa/setup — 新規 secret 生成 (pending state) + otpauth URI 返却 */
export const setupTwoFactor = (): Promise<TwoFactorSetupResponse> =>
  authedFetch<TwoFactorSetupResponse>(`${BASE}/setup`, { method: 'POST' })

/** POST /auth/2fa/verify-enable — pending state を verify、enabled=true + recovery codes 発行 */
export const verifyEnableTwoFactor = (code: string): Promise<TwoFactorEnableResponse> =>
  authedFetch<TwoFactorEnableResponse>(`${BASE}/verify-enable`, {
    method: 'POST',
    body: { code },
  })

/** POST /auth/2fa/disable — secret + recovery codes クリア */
export const disableTwoFactor = (code: string): Promise<TwoFactorStatusResponse> =>
  authedFetch<TwoFactorStatusResponse>(`${BASE}/disable`, {
    method: 'POST',
    body: { code },
  })

/** POST /auth/2fa/disable/email-code/request — 登録メールへ 6 桁 一時コード送信 (204) */
export const requestTwoFactorDisableEmailCode = (): Promise<void> =>
  authedFetch<void>(`${BASE}/disable/email-code/request`, { method: 'POST' })

/** POST /auth/2fa/disable/email-code/verify — メール受信した 6 桁 code で 2FA 解除 */
export const verifyTwoFactorDisableEmailCode = (
  code: string,
): Promise<TwoFactorStatusResponse> =>
  authedFetch<TwoFactorStatusResponse>(`${BASE}/disable/email-code/verify`, {
    method: 'POST',
    body: { code },
  })

/** POST /auth/2fa/recovery-codes/regenerate — 新規 10 個発行 + 旧 codes 上書き */
export const regenerateTwoFactorRecoveryCodes = (
  code: string,
): Promise<TwoFactorRegenerateResponse> =>
  authedFetch<TwoFactorRegenerateResponse>(`${BASE}/recovery-codes/regenerate`, {
    method: 'POST',
    body: { code },
  })

/**
 * POST /auth/2fa/login-verify — SRP login で受け取った pre-auth token + 6 桁コードを送り、
 * access token と protected SK を取得する。
 *
 * pre-auth token は `Authorization: Bearer <token>` で送信 (短期 JWT、5 分 TTL)。
 * `authedFetch` は session の access token を使うため、ここでは `apiPost` + bearer を直使用。
 */
export const loginVerifyTwoFactor = (
  code: string,
  preAuthToken: string,
  trustDevice?: boolean,
): Promise<TwoFactorLoginVerifyResponse> =>
  apiPost<TwoFactorLoginVerifyResponse, { code: string; trustDevice?: boolean }>(
    `${BASE}/login-verify`,
    // `trustDevice` 未指定はサーバ側で `true` 扱い (後方互換)。明示的に渡された
    // 場合のみ body に載せ、不要なフィールドで wire を膨らませない。
    trustDevice === undefined ? { code } : { code, trustDevice },
    { bearerToken: preAuthToken },
  )

/**
 * POST /auth/2fa/email-recovery/request — pre-auth bearer + 登録メールアドレスへ 6 桁
 * 一時コード送信。レスポンスは 204 No Content (列挙されない)。
 */
export const requestTwoFactorEmailRecovery = (preAuthToken: string): Promise<void> =>
  apiPost<void, Record<string, never>>(
    `${BASE}/email-recovery/request`,
    {},
    { bearerToken: preAuthToken },
  )

/**
 * POST /auth/2fa/email-recovery/verify — pre-auth bearer + body `{code, trustDevice?}`。
 * 成功時 login-verify と同じ shape を返す (access/refresh + protected SK)。
 */
export const verifyTwoFactorEmailRecovery = (
  code: string,
  preAuthToken: string,
  trustDevice?: boolean,
): Promise<TwoFactorLoginVerifyResponse> =>
  apiPost<TwoFactorLoginVerifyResponse, { code: string; trustDevice?: boolean }>(
    `${BASE}/email-recovery/verify`,
    trustDevice === undefined ? { code } : { code, trustDevice },
    { bearerToken: preAuthToken },
  )
